import type {Constructor} from "type-fest";
import {equals} from "../../helpers/Equatable";
import type {Optional} from "../../helpers/TypeHelpers";
import type {
  SelectOption,
  SelectOptionGroup,
  SelectOptionGroupKey,
  SelectOptionLabelProvider,
  SelectOptions,
  SelectOptionsGrouping,
  SelectOptionValueProviders,
} from "./SelectOptions";
import {toSelectOption, toSelectOptions} from "./SelectOptions";

export type IdentifiableModelId = string | number;

export interface IdentifiableModel<TId extends IdentifiableModelId = number> {
  id?: TId;
}

export interface NumberedIdentifiableModel<TNumber extends number = number> extends IdentifiableModel<TNumber> {
  id: TNumber;
  number: TNumber;
}

export function equalIds<TModel extends IdentifiableModel<IdOf<TModel>>>(lhs: TModel, rhs: TModel): boolean {
  return equals(lhs.id, rhs.id);
}

export type IdOf<T> = T extends IdentifiableModel<infer U> ? U : never;

export type Existing<TModel extends IdentifiableModel<IdentifiableModelId>> =
  TModel extends IdentifiableModel<infer Id> ? TModel & Required<IdentifiableModel<Id>> : never;
export type New<TModel extends IdentifiableModel<IdentifiableModelId>> = TModel & { id?: undefined };

export type NewOrExisting<TModel extends IdentifiableModel<IdentifiableModelId>> = New<TModel> | Existing<TModel>;

export function isNew<
  TModel extends IdentifiableModel<IdentifiableModelId>,
>(model: TModel): model is New<TModel> {
  return model.id === undefined;
}

export function asNew<TModel extends IdentifiableModel<IdentifiableModelId>>(model: TModel): New<TModel> {
  if (isNew(model)) return model;
  model.id = undefined;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return model as New<TModel>;
}

export function createNew<TModel extends IdentifiableModel<IdentifiableModelId>, TParams extends unknown[] = unknown[]>(
  modelClass: Constructor<TModel, TParams>,
  ...params: TParams
): New<TModel> {
  return asNew(new modelClass(...params));
}

export function isExisting<
  TModel extends IdentifiableModel<IdentifiableModelId>
>(model: TModel): model is Existing<TModel> {
  return model.id !== undefined;
}

export class InexistentModelError<TModel extends IdentifiableModel<IdentifiableModelId>> extends Error {
  public constructor(model: TModel) {
    super(`Expected model of type ${model.constructor.name} to have an id!`);
  }
}

function unsafeAssertExists<
  TModel extends IdentifiableModel<IdentifiableModelId>,
// eslint-disable-next-line @typescript-eslint/no-unused-vars
>(model: TModel): asserts model is Existing<TModel> {
  // no-op
}

function assertExists<
  TModel extends IdentifiableModel<IdentifiableModelId>,
>(model: TModel): asserts model is Existing<TModel> {
  if (!isExisting(model)) throw new InexistentModelError(model);
}

export function verifyExists<
  TModel extends IdentifiableModel<IdentifiableModelId>,
>(model: TModel): Existing<TModel> {
  assertExists(model);
  return model;
}

export function createExisting<
  TModel extends object & IdentifiableModel<TIdType>,
  TIdType extends IdentifiableModelId = IdOf<TModel>,
>(
  modelClass: Constructor<TModel, []> | Constructor<TModel, [Optional<TIdType>]> | Constructor<TModel, [TIdType]>,
  id: TIdType,
): Existing<TModel>;
export function createExisting<
  TModel extends object & IdentifiableModel<TIdType>,
  TParams extends [],
  TIdType extends IdentifiableModelId = IdOf<TModel>,
>(
  modelClass: Constructor<TModel, [Optional<TIdType>, ...TParams]> | Constructor<TModel, [TIdType, ...TParams]>,
  id: TIdType,
  ...params: TParams
): Existing<TModel>;
export function createExisting<
  TModel extends object & IdentifiableModel<TIdType>,
  TParams extends [],
  TIdType extends IdentifiableModelId = IdOf<TModel>,
>(
  modelClass: Constructor<TModel, []>
    | Constructor<TModel, [Optional<TIdType>]>
    | Constructor<TModel, [Optional<TIdType>, ...TParams]>,
  id: TIdType,
  ...params: TParams
): Existing<TModel> {
  function isEmptyConstructor(
    constr: Constructor<TModel, []>
      | Constructor<TModel, [Optional<TIdType>]>
      | Constructor<TModel, [Optional<TIdType>, ...TParams]>,
  ): constr is Constructor<TModel, []> {
    return constr.length === 0;
  }

  let model: TModel;
  if (isEmptyConstructor(modelClass)) {
    model = new modelClass();
    model.id = id;
  } else {
    model = new modelClass(id, ...params);
  }
  unsafeAssertExists(model);
  return model;
}

export function selectOptionValueProviders<
  TModel extends object & IdentifiableModel<TIdType>,
  TIdType extends IdentifiableModelId = IdOf<TModel>,
>(
  labelProvider: SelectOptionLabelProvider<Readonly<Existing<TModel>>>,
): SelectOptionValueProviders<Readonly<Existing<TModel>>, TIdType> {
  return {value: m => m.id, label: labelProvider};
}

export function toIdSelectOption<
  TModel extends object & IdentifiableModel<TIdType>,
  TIdType extends IdentifiableModelId = IdOf<TModel>,
>(
  model: Readonly<Existing<TModel>>,
  labelProvider: SelectOptionLabelProvider<Readonly<Existing<TModel>>>,
): SelectOption<TIdType> {
  return toSelectOption(model, selectOptionValueProviders(labelProvider));
}

// @ts-expect-error: It's not, in fact, infinite...
export function toIdSelectOptions<
  TModel extends object & IdentifiableModel<TIdType>,
  TIdType extends IdentifiableModelId = IdOf<TModel>,
>(
  models: readonly Readonly<Existing<TModel>>[],
  labelProvider: SelectOptionLabelProvider<Readonly<Existing<TModel>>>,
): readonly SelectOption<TIdType>[];
export function toIdSelectOptions<
  TModel extends object & IdentifiableModel<TIdType>,
  TGroupKey extends SelectOptionGroupKey,
  TIdType extends IdentifiableModelId = IdOf<TModel>,
>(
  models: readonly Readonly<Existing<TModel>>[],
  labelProvider: SelectOptionLabelProvider<Readonly<Existing<TModel>>>,
  grouping: SelectOptionsGrouping<Readonly<Existing<TModel>>, TGroupKey>,
): readonly SelectOptionGroup<TIdType, TGroupKey>[];
export function toIdSelectOptions<
  TModel extends object & IdentifiableModel<TIdType>,
  TGroupKey extends SelectOptionGroupKey | undefined,
  TIdType extends IdentifiableModelId = IdOf<TModel>,
>(
  models: readonly Readonly<Existing<TModel>>[],
  labelProvider: SelectOptionLabelProvider<Readonly<Existing<TModel>>>,
  grouping?: SelectOptionsGrouping<Readonly<Existing<TModel>>, TGroupKey>,
): SelectOptions<TIdType, TGroupKey>;
export function toIdSelectOptions<
  TModel extends object & IdentifiableModel<TIdType>,
  TGroupKey extends SelectOptionGroupKey | undefined,
  TIdType extends IdentifiableModelId = IdOf<TModel>,
>(
  models: readonly Readonly<Existing<TModel>>[],
  labelProvider: SelectOptionLabelProvider<Readonly<Existing<TModel>>>,
  grouping?: SelectOptionsGrouping<Readonly<Existing<TModel>>, TGroupKey>,
): SelectOptions<TIdType, TGroupKey> {
  return toSelectOptions(models, selectOptionValueProviders(labelProvider), grouping);
}
